import { CheckCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CaseInspection, CaseStatus, CaseType } from "@/api/rest/models/warranty";
import { getAllCases } from "@/api/rest/services/warrantyService";
import { inspectionDefects, isCompleted } from "@/utils/inspectionGroups";

/** Shows which inspection a defect came from and links to its sibling defects, in act order. */
export default function InspectionDefects({
  inspection,
  householdId,
  currentCaseId,
  showExternalNumber,
}: {
  inspection: CaseInspection;
  householdId: number;
  currentCaseId: number;
  showExternalNumber: boolean;
}) {
  const { t } = useTranslation();
  const { data: defects } = useQuery({
    queryKey: ["inspection-defects", householdId, inspection.id],
    queryFn: async () => {
      const response = await getAllCases(householdId, CaseType.WARRANTY);
      if (!response.success) {
        throw new Error(response.message || "Failed to load inspection defects");
      }
      return inspectionDefects(response.result, inspection.id);
    },
  });

  return (
    <nav aria-label={t("warrantyInspection.defects")} className="flex flex-col gap-2 ml-12 pb-4 max-w-[800px]">
      <h2 className="text-xl">
        <strong>{t("warrantyInspection.title")}: </strong>
        {new Date(inspection.inspectedAt).toLocaleDateString()}
        {showExternalNumber && inspection.externalNumber && (
          <span className="text-gray-500 ml-2">(Hausing #{inspection.externalNumber})</span>
        )}
        {defects && (
          <span className="text-gray-500 ml-2">
            · {t("warrantyInspection.progress", { done: defects.filter(isCompleted).length, total: defects.length })}
          </span>
        )}
      </h2>
      {defects && defects.length > 1 && (
        <ol className="flex flex-col gap-1 border-l-2 border-orange-200 pl-3">
          {defects.map((d) => {
            const isCurrent = d.id === currentCaseId;
            return (
              <li key={d.id}>
                <Link
                  to={`/warranty/${d.id}`}
                  aria-current={isCurrent ? "page" : undefined}
                  className={clsx(
                    "flex flex-row items-center gap-2 rounded-md px-2 py-1",
                    isCurrent ? "bg-orange-100 font-bold" : "hover:bg-orange-50",
                  )}
                >
                  {d.status === CaseStatus.SOLVED && <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-500" />}
                  {d.status === CaseStatus.CLOSED && <MinusCircleIcon className="h-4 w-4 shrink-0 text-red-600" />}
                  {!isCompleted(d) && <span className="h-4 w-4 shrink-0" aria-hidden />}
                  <span className="flex-1">{d.title}</span>
                  <span className="text-sm font-normal text-gray-500">{t(d.localisedStatus)}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
}
